// frontend/src/components/Individual/History.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Toast, { useToast } from '../Shared/Toast';

const History = ({ user, onLogout }) => {
  const [transcripts, setTranscripts] = useState([]);
  const [groupedSessions, setGroupedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const { toast, showToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTranscripts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTranscripts = async () => {
    try {
      setError('');
      const response = await api.get('/transcripts');
      const data = response.data.transcripts || [];
      setTranscripts(data);
      groupBySession(data);
    } catch (error) {
      console.error('Error fetching transcripts:', error.response?.status, error.response?.data);
      setError('Failed to load transcripts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const groupBySession = (data) => {
    const groups = {};
    data.forEach((t) => {
      const date = new Date(t.createdAt).toLocaleDateString();
      const key = `${t.roomId || 'direct'}_${date}`;

      if (!groups[key]) {
        groups[key] = {
          key,
          roomId: t.roomId,
          date,
          sessionKey: t.sessionKey,
          sentences: [],
          firstCreatedAt: t.createdAt,
          lastCreatedAt: t.createdAt,
          ids: []
        };
      }

      groups[key].sentences.push(t.content);
      groups[key].ids.push(t.id);
      if (new Date(t.createdAt) > new Date(groups[key].lastCreatedAt)) {
        groups[key].lastCreatedAt = t.createdAt;
      }
    });

    const sorted = Object.values(groups).sort(
      (a, b) => new Date(b.lastCreatedAt) - new Date(a.lastCreatedAt)
    );
    setGroupedSessions(sorted);
  };

  const handleDelete = async (ids) => {
    if (!window.confirm('Delete this entire session transcript?')) return;
    try {
      await Promise.all(ids.map(id => api.delete(`/transcripts/${id}`)));
      showToast('Transcript deleted', 'success');
      fetchTranscripts();
    } catch (error) {
      showToast('Failed to delete transcript', 'error');
    }
  };

  const handleCopyTranscript = (sentences) => {
    navigator.clipboard.writeText(sentences.join(' '));
    showToast('Transcript copied to clipboard!', 'success');
  };

  const filteredSessions = groupedSessions.filter(session =>
    session.sentences.join(' ').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (session.roomId && session.roomId.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} />

      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button onClick={() => navigate('/dashboard')} className="mr-4 text-gray-600 hover:text-gray-900">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-800">Transcript History</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</p>
              </div>
              <button onClick={onLogout} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{groupedSessions.length}</p>
            <p className="text-sm text-gray-500">Sessions</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{transcripts.length}</p>
            <p className="text-sm text-gray-500">Total Sentences</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search transcripts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <svg className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={fetchTranscripts} className="mt-2 text-sm text-red-600 underline">Retry</button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No transcripts found</h3>
            <p className="text-gray-500">Start a recording session to create your first transcript!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSessions.map((session) => (
              <div
                key={session.key}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedSession(session)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        {session.date}
                      </span>
                      {session.roomId && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full font-mono">
                          Room: {session.roomId.slice(0, 12)}...
                        </span>
                      )}
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {session.sentences.length} sentence{session.sentences.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <p className="text-gray-800 line-clamp-2">
                      {session.sentences.join(' ')}
                    </p>

                    <p className="text-sm text-gray-500 mt-2">
                      {new Date(session.firstCreatedAt).toLocaleTimeString()} — {new Date(session.lastCreatedAt).toLocaleTimeString()}
                    </p>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(session.ids); }}
                    className="ml-4 text-red-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Session Detail Modal */}
      {selectedSession && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedSession(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Session Transcript</h3>
                  <p className="text-sm text-gray-500">{selectedSession.date} · {selectedSession.sentences.length} sentences</p>
                  {selectedSession.roomId && (
                    <p className="text-xs text-gray-400 mt-1 font-mono">Room: {selectedSession.roomId}</p>
                  )}
                </div>
                <button onClick={() => setSelectedSession(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-6 mb-4">
                {selectedSession.sentences.map((sentence, i) => (
                  <p key={i} className="text-gray-800 mb-2 pb-2 border-b border-gray-200 last:border-0 last:mb-0 last:pb-0">
                    {sentence}
                  </p>
                ))}
              </div>

              <button
                onClick={() => handleCopyTranscript(selectedSession.sentences)}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
              >
                Copy Full Transcript
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
